import type {
  DurableIncrement,
  EphemeralIncrement,
} from "@excalidraw/element/store";
import { TOOLS } from "./posthog";
import type { Tool } from "./posthog";

const IGNORED = new Set([
  "version",
  "versionNonce",
  "updated",
  "seed",
  "index",
]);
/** Translate local durable deltas into one anonymous semantic operation. */
export class EditAnalytics {
  private seen = new WeakSet<DurableIncrement>();
  private gestureTool?: "drawShape";
  private pending?: "undo" | "redo" | "paste";
  /** Keep the transport independent from scene data. */
  constructor(private readonly used: (tool: Tool) => void) {}
  /** Label the next committed delta while preserving no-op suppression. */
  intent(tool: "undo" | "redo" | "paste"): void {
    this.pending = tool;
  }
  /** A new input ends an earlier intent that produced no committed change. */
  clearIntent(): void {
    this.pending = undefined;
    this.gestureTool = undefined;
  }
  /** Preserve the originating smart-shape tool while inspecting its final geometry. */
  beginGesture(tool: string): void {
    this.gestureTool = tool === "drawShape" ? "drawShape" : undefined;
  }
  /** Ignore ephemeral synchronization and inspect only changed semantic fields locally. */
  capture(event: DurableIncrement | EphemeralIncrement): void {
    if (event.type !== "durable") {
      return;
    }
    const durable = event as DurableIncrement;
    if (this.seen.has(durable)) {
      return;
    }
    this.seen.add(durable);
    const { added, removed, updated } = durable.delta.elements;
    const created = Object.values(added).filter(
      ({ inserted }) => !inserted.isDeleted,
    );
    const changes = Object.values(updated).flatMap(({ inserted, deleted }) =>
      Object.keys(inserted).filter(
        (key) =>
          !IGNORED.has(key) &&
          JSON.stringify(inserted[key as keyof typeof inserted]) !==
            JSON.stringify(deleted[key as keyof typeof deleted]),
      ),
    );
    let tool: Tool | undefined;
    if (Object.keys(removed).length || changes.includes("isDeleted")) {
      tool = "delete";
    } else if (created.length) {
      const type = this.gestureTool ?? created[0].inserted.type;
      tool = type && TOOLS.includes(type as Tool) ? (type as Tool) : "create";
    } else if (changes.includes("text") || changes.includes("originalText")) {
      tool = "text";
    } else if (
      changes.includes("width") ||
      changes.includes("height") ||
      changes.includes("points")
    ) {
      tool = "resize";
    } else if (changes.includes("angle")) {
      tool = "rotate";
    } else if (changes.includes("x") || changes.includes("y")) {
      tool = "move";
    } else if (changes.length) {
      tool = "edit";
    }
    if (!tool) {
      this.pending = undefined;
      return;
    }
    if (this.pending) {
      tool = this.pending;
    }
    this.pending = undefined;
    this.gestureTool = undefined;
    this.used(tool);
  }
}
